import { useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import {
  useAdminInventoryBalances,
  useAdminInventoryMovements,
  useAdminInventoryWarehouses,
} from "@/lib/api/inventory.queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Table, THead, TH, TBody, TR, TD } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { formatDate } from "@/lib/utils";
import { useMoneyFormatter } from "@/lib/money-format";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("es-CO").format(n);
}

const KIND_LABEL: Record<string, string> = {
  initial_load: "Carga inicial",
  purchase: "Compra",
  sale: "Venta",
  sale_return: "Dev. cliente",
  purchase_return: "Dev. proveedor",
  adjustment_in: "Ajuste +",
  adjustment_out: "Ajuste -",
  transfer_in: "Traslado in",
  transfer_out: "Traslado out",
  write_off: "Baja",
};

interface InventoryPanelProps {
  companyId: string;
}

export function InventoryPanel({ companyId }: InventoryPanelProps) {
  const fmtCop = useMoneyFormatter();
  const [warehouseId, setWarehouseId] = useState<string>("");
  const warehousesQuery = useAdminInventoryWarehouses(companyId);
  const balancesQuery = useAdminInventoryBalances(companyId, {
    warehouseId: warehouseId || undefined,
  });
  const movementsQuery = useAdminInventoryMovements(companyId, { limit: 50 });

  if (warehousesQuery.isPending || balancesQuery.isPending) {
    return <CenteredSpinner label="Cargando inventario…" />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-xs text-slate-500 font-normal">
              Valor total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold text-slate-900">
              {fmtCop(balancesQuery.data?.totals.totalValue ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-xs text-slate-500 font-normal">
              Líneas con saldo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold text-slate-900">
              {balancesQuery.data?.totals.count ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-xs text-slate-500 font-normal">
              Bodegas activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-semibold text-slate-900">
              {(warehousesQuery.data?.warehouses ?? []).filter((w) => w.isActive)
                .length}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bodegas</CardTitle>
        </CardHeader>
        <CardContent>
          {(warehousesQuery.data?.warehouses ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">
              Esta empresa aún no tiene bodegas.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Código</TH>
                  <TH>Nombre</TH>
                  <TH>Estado</TH>
                  <TH>Creada</TH>
                </TR>
              </THead>
              <TBody>
                {warehousesQuery.data!.warehouses.map((w) => (
                  <TR key={w.id}>
                    <TD className="font-mono text-xs">{w.code}</TD>
                    <TD>{w.name}</TD>
                    <TD className="space-x-1">
                      {w.isDefault && <Badge tone="brand">Predeterminada</Badge>}
                      {w.isActive ? (
                        <Badge tone="success">Activa</Badge>
                      ) : (
                        <Badge tone="neutral">Archivada</Badge>
                      )}
                    </TD>
                    <TD className="text-xs text-slate-500">
                      {formatDate(w.createdAt)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Existencias</CardTitle>
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Todas las bodegas</option>
              {(warehousesQuery.data?.warehouses ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {balancesQuery.isError ? (
            <ErrorState
              error={balancesQuery.error as Error}
              onRetry={() => void balancesQuery.refetch()}
            />
          ) : (balancesQuery.data?.balances.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">
              Sin existencias para los filtros seleccionados.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Producto</TH>
                  <TH>SKU</TH>
                  <TH>Bodega</TH>
                  <TH className="text-right">Cantidad</TH>
                  <TH className="text-right">Costo unit.</TH>
                  <TH className="text-right">Valor</TH>
                </TR>
              </THead>
              <TBody>
                {balancesQuery.data!.balances.map((b) => (
                  <TR key={`${b.productId}:${b.warehouseId}`}>
                    <TD>{b.productName}</TD>
                    <TD className="text-xs text-slate-500">
                      {b.productSku ?? "—"}
                    </TD>
                    <TD>
                      <Badge tone="neutral">{b.warehouseCode}</Badge>
                    </TD>
                    <TD className="text-right tabular-nums">{fmtQty(b.qty)}</TD>
                    <TD className="text-right tabular-nums">
                      {fmtCop(b.avgUnitCost)}
                    </TD>
                    <TD className="text-right tabular-nums font-medium">
                      {fmtCop(b.totalValue)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Últimos movimientos (50)</CardTitle>
        </CardHeader>
        <CardContent>
          {movementsQuery.isPending ? (
            <CenteredSpinner label="Cargando movimientos…" />
          ) : movementsQuery.isError ? (
            <ErrorState
              error={movementsQuery.error as Error}
              onRetry={() => void movementsQuery.refetch()}
            />
          ) : (movementsQuery.data?.movements.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-500">Sin movimientos registrados.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Fecha</TH>
                  <TH>Tipo</TH>
                  <TH>Producto</TH>
                  <TH>Bodega</TH>
                  <TH className="text-right">Qty</TH>
                  <TH className="text-right">Costo unit.</TH>
                  <TH className="text-right">Saldo qty</TH>
                  <TH>Origen</TH>
                </TR>
              </THead>
              <TBody>
                {movementsQuery.data!.movements.map((m) => (
                  <TR key={m.id}>
                    <TD className="text-xs text-slate-500">
                      {formatDate(m.createdAt)}
                    </TD>
                    <TD>
                      <span className="inline-flex items-center gap-1">
                        {m.direction === "in" ? (
                          <ArrowDown className="h-3 w-3 text-emerald-600" />
                        ) : (
                          <ArrowUp className="h-3 w-3 text-rose-600" />
                        )}
                        {KIND_LABEL[m.kind] ?? m.kind}
                      </span>
                    </TD>
                    <TD>
                      {m.productName ?? "—"}
                      {m.productSku && (
                        <span className="ml-1 text-xs text-slate-500">
                          {m.productSku}
                        </span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone="neutral">{m.warehouseCode ?? "?"}</Badge>
                    </TD>
                    <TD className="text-right tabular-nums">{fmtQty(m.qty)}</TD>
                    <TD className="text-right tabular-nums">
                      {fmtCop(m.unitCost)}
                    </TD>
                    <TD className="text-right tabular-nums text-slate-500">
                      {fmtQty(m.qtyAfter)}
                    </TD>
                    <TD className="text-xs text-slate-500">
                      {m.sourceKind}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
